import { LayoutGridIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover';
import { useSidebar } from '@/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/tooltip';
import {
  getAccessibleServices,
  getServiceUrl,
  type ServiceEntry,
} from './service-registry';

interface ServiceMarketplaceProps {
  currentServiceId: string;
  orgId: string;
}

function ServiceMarketplace({
  currentServiceId,
  orgId,
}: ServiceMarketplaceProps) {
  const { isMobile, state } = useSidebar();
  const services = getAccessibleServices(orgId);

  if (services.length <= 1) return null;

  const isCollapsed = state === 'collapsed';

  const triggerButton = (
    <PopoverTrigger asChild>
      <button
        type="button"
        className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-primary hover:text-sidebar-primary-foreground transition-colors"
        aria-label="应用中心"
      >
        <LayoutGridIcon className="size-4" />
      </button>
    </PopoverTrigger>
  );

  return (
    <Popover>
      {isCollapsed && !isMobile ? (
        <Tooltip>
          <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
          <TooltipContent side="right" align="center">
            应用中心
          </TooltipContent>
        </Tooltip>
      ) : (
        triggerButton
      )}
      <PopoverContent
        side={isMobile ? 'bottom' : 'right'}
        align="start"
        sideOffset={8}
        className="w-80 p-3"
      >
        <p className="mb-2 px-1 text-xs font-medium text-muted-foreground">
          应用中心
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {services.map((service) => (
            <ServiceTile
              key={service.id}
              service={service}
              isCurrent={service.id === currentServiceId}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ServiceTile({
  service,
  isCurrent,
}: {
  service: ServiceEntry;
  isCurrent: boolean;
}) {
  const Icon = service.icon;
  const url = getServiceUrl(service);

  return (
    <a
      href={isCurrent ? undefined : url}
      aria-current={isCurrent ? 'page' : undefined}
      className={`flex flex-col items-center gap-1.5 rounded-lg p-3 text-center transition-colors ${
        isCurrent
          ? 'bg-primary/10 ring-1 ring-primary/20 cursor-default'
          : 'hover:bg-muted cursor-pointer'
      }`}
    >
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-lg ${
          isCurrent
            ? 'bg-primary text-primary-foreground'
            : 'bg-primary/10 text-primary'
        }`}
      >
        <Icon className="h-4.5 w-4.5" />
      </div>
      <span className="text-xs font-medium leading-tight">{service.name}</span>
      <span className="text-[10px] leading-tight text-muted-foreground">
        {service.description}
      </span>
    </a>
  );
}

export type { ServiceMarketplaceProps };
export { ServiceMarketplace };
